import {useEffect, useState} from "react"
import {Event} from "../../../../dtos/eventResponse"
import {ModifiedEvent} from "./types"
import {WorkEntityServiceDependency} from "../../../../services/work/WorkEntityService"

const extractRevisionSegment = (url: string): string => {
	const match = url.match(/\/revisions\/([^\/]+)/)
	return match?.[1] ?? ""
}

const matchWorkRevisionId = (workRevisions: any[], docRefUrl: string): string => {
	const revisionSegment = extractRevisionSegment(docRefUrl)

	const matched = workRevisions
		.filter((rev) => rev.design?.designUrl?.includes(revisionSegment))
		.sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime())

	return matched[0]?.workRevisionId
}

const fetchPreviewUrls = async (docRefUrl: string): Promise<string[]> => {
	try {
		const res = await fetch(docRefUrl)
		if (!res.ok) throw new Error(`Failed to fetch docRefUrl: ${res.status}`)
		const docInfo = await res.json()

		const drawingHref = docInfo?._links?.drawingInstructions?.href
		if (!drawingHref) return []

		const previewRes = await fetch(drawingHref)
		if (!previewRes.ok) throw new Error(`Failed to fetch preview data: ${previewRes.status}`)
		const pagesData = await previewRes.json()

		return (pagesData?.Pages ?? [])
			.flatMap((page: any) => page?.Previews ?? [])
			.map((preview: any) => preview?.Url)
			.filter((url: string | undefined): url is string => !!url)
	} catch (err) {
		console.error("Error fetching preview URLs:", err)
		return []
	}
}

export const useDocumentEditHistory = (workId: string | null, itemKeyEvents?: Event[]) => {
	const [events, setEvents] = useState<ModifiedEvent[]>([])

	useEffect(() => {
		const processEvents = async () => {
			if (!Array.isArray(itemKeyEvents) || itemKeyEvents.length === 0) return

			const workRevisions = workId
				? (await WorkEntityServiceDependency.getAllWorkRevisions(workId)) ?? []
				: []
			const results: ModifiedEvent[] = []
			for (const event of itemKeyEvents) {
				try {
					const type = event?.type
					const eventProps = event?.eventProperties
					const shopperId = event?.shopperId ?? null
					if (
						type === "LINE_ITEM_UPDATED" &&
						eventProps?.eventSubType === "documentReviewApproved"
					) {
						const mcpEventStr = eventProps?.mcpEvent?.[0]
						const parsedMcpEvent = mcpEventStr ? JSON.parse(mcpEventStr) : {}

						const docRefUrl =
							parsedMcpEvent?.details?.[0]?.additionalData?.metadata?.find(
								(item: {name: string}) => item.name === "documentReferenceUrl"
							)?.value ??
							parsedMcpEvent?.documentReviewApprovedDetails?.[0]?.updatedDocumentReferenceUrl

						const eventDate = parsedMcpEvent?.eventDate ?? ""

						if (docRefUrl) {
							const previewUrls = await fetchPreviewUrls(docRefUrl)
							results.push({
								docrefURL: previewUrls,
								date: eventDate,
								owner: "Doc Review",
								workRevisionId: matchWorkRevisionId(workRevisions, docRefUrl),
								workId: workId,
								shopperId: shopperId,
							})
						}
					} else if (type === "LINE_ITEM_UPDATED" && eventProps?.updateType === "DOCUMENT") {
						const updates = event?.itemInfo?.[0]?.itemProperties?.updates
						const docRefUrl = updates?.docRefUrl ?? null

						if (docRefUrl) {
							results.push({
								docrefURL: docRefUrl,
								date: event?.dateTime ?? "",
								owner: "Care Specialist",
								livePreviewUrl: updates?.livePreviewUrl ?? "",
								originalLivePreviewUrl: updates?.originalLivePreviewUrl ?? "",
								workRevisionId: matchWorkRevisionId(workRevisions, docRefUrl),
								workId: workId,
								shopperId: shopperId,
							})
						}
					}
				} catch (err) {
					console.error("Error processing event:", err)
				}
			}

			results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
			setEvents(results)
		}

		processEvents()
	}, [itemKeyEvents, workId])

	return {events, latestVersion: events[0] ?? null}
}
